import React from "react";
import { Shield, Phone, Mail, Award } from "lucide-react";
import { ABOUT_US_TEXT } from "../data";
import { useLanguage } from "../components/LanguageContext";
import SEO from "../components/SEO";
import ImagePlaceholder from "../components/ImagePlaceholder";
import {
  DraftGrid,
  CornerBrackets,
  OrganicLines,
  DecorativeDivider,
  HorseLineArt
} from "../components/VectorDecor";

export default function AboutUs() {
  const { lang, t } = useLanguage();

  return (
    <div className="relative bg-[#FCF9F5] text-stone-900 py-24 font-sans-custom overflow-hidden">
      <SEO pageKey="about" />
      
      {/* Subtle Background Vector Elements */}
      <DraftGrid className="opacity-[0.06] text-stone-900" />
      <OrganicLines className="absolute right-0 top-40 w-[40%] h-auto text-stone-400/20 pointer-events-none select-none" />
      <HorseLineArt className="absolute left-10 bottom-20 w-80 h-80 text-stone-900/[0.04] pointer-events-none select-none" />

      <div className="mx-auto max-w-7xl px-6 md:px-12 relative z-10">
        {/* Header */}
        <div className="max-w-3xl space-y-6 mb-10 md:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-3 border border-stone-300 bg-stone-100 px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] text-stone-800 rounded-none font-medium font-sans-custom">
            <Shield className="h-3.5 w-3.5 text-stone-700" />
            <span>{t.about.badge}</span>
          </div>
          <h1 className="font-display text-3xl sm:text-5xl md:text-6xl uppercase tracking-normal sm:tracking-wide-custom text-stone-900 font-medium">
            {t.about.title}{" "}
            <span className="italic text-stone-600 lowercase font-display normal-case">
              {t.about.title_italic}
            </span>
          </h1>
        </div>

        {/* Story Section */}
        <div className="grid gap-12 lg:grid-cols-12 mb-28 items-center">
          <div className="lg:col-span-7 space-y-6 text-sm leading-relaxed text-stone-600 font-light tracking-wide-custom font-sans-custom">
            {t.about.paragraphs.map((para, idx) => {
              if (para.trim().startsWith("•")) {
                return (
                  <div key={idx} className="flex items-start gap-3 pl-4 py-1">
                    <span className="text-stone-400 mt-1.5 text-xs">•</span>
                    <span className="font-light">{para.replace(/^[•\s]+/, "")}</span>
                  </div>
                );
              }
              return <p key={idx}>{para}</p>;
            })}
          </div>
          <div className="lg:col-span-5 relative aspect-[3/4] overflow-hidden border border-stone-200 bg-stone-100 rounded-none shadow-sm group">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/47342516-e670-4852-ad73-fdec3014e752/image00042.jpeg"
              alt="Equinesport"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
            />
            
            {/* Corner Brackets for Image Frame - Positioned after the image to render on top */}
            <CornerBrackets className="text-stone-400 group-hover:text-stone-600 transition-colors duration-500 z-20" size="w-5 h-5" />
            
            <div className="absolute inset-0 bg-gradient-to-t from-stone-900/40 via-transparent to-transparent z-10" />
            <div className="absolute bottom-4 left-4 flex items-center gap-2 text-[10px] uppercase tracking-widest text-white font-semibold font-sans-custom z-20">
              <Award className="h-4 w-4 text-stone-200" />
              <span>Equinesport, Denmark</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
